'use strict'

const { nextCopyUniqueTitle } = require('./page-copy')
const {
  findPageRecordSpreadSheet,
  pageRecordRelationship,
  recordSpreadSheetsOnPage
} = require('./page-record-link')

const VIEW_TYPES = new Set([
  'table',
  'pivot',
  'cards',
  'card',
  'form',
  'login',
  'registration',
  'menu',
  'maps',
  'calendar',
  'map',
  'custom',
  'carousel',
  'chart',
  'richtext',
  'staticimage'
])

const IDENTIFIER = /^[A-Za-z_][A-Za-z0-9_]*$/

const TITLE_REQUIRED = 'a unique title is required'
const TITLE_REUSE = 'choose a new unique title'
const TITLE_COLLISION = 'that unique title is already used'
const TITLE_INVALID = 'unique title must start with a letter or an underscore, then letters, digits, and underscores'
const CHOOSE_PAGE = 'choose a page'
const DIFFERENT_PAGE = 'choose a different page'
const PAGE_INELIGIBLE = 'that page does not use this spreadsheet or a linked one'
const NO_PAGE = 'no page uses this spreadsheet or a linked one'
const VIEW_MISSING = 'view not found on this page'
const PAGE_MISSING = 'page not found'
const MODE_INVALID = 'choose duplicate or move'

function isViewType (type) {

  return VIEW_TYPES.has(type)
}

function walkComponents (components, visit) {

  for (const component of components || []) {

    if (!component || typeof component !== 'object') continue

    visit(component)
    walkComponents(component.components, visit)
  }
}

function findView (page, viewUniqueTitle) {

  let found = null

  walkComponents(page && page.components, (component) => {

    if (found) return

    if (component.uniqueTitle === viewUniqueTitle && isViewType(component.type)) found = component
  })

  return found
}

function takenComponentUniqueTitles (pages) {

  const taken = new Set()

  for (const page of pages || []) {

    walkComponents(page && page.components, (component) => {

      if (component.uniqueTitle) taken.add(String(component.uniqueTitle))
    })
  }

  return taken
}

function takenViewTitles (pages) {

  const taken = new Set()

  for (const page of pages || []) {

    walkComponents(page && page.components, (component) => {

      if (!isViewType(component.type) || component.title == null || component.title === '') return

      taken.add(String(component.title))
    })
  }

  return taken
}

function suggestedViewUniqueTitle (pages, sourceUniqueTitle) {

  const taken = takenComponentUniqueTitles(pages)
  const source = sourceUniqueTitle ? String(sourceUniqueTitle) : ''

  if (source) taken.add(source)

  const base = source && IDENTIFIER.test(source) ? source : 'view'

  return nextCopyUniqueTitle(base, taken)
}

function nextViewTitle (title, taken) {

  const label = title == null || String(title).trim() === '' ? 'View' : String(title).trim()
  const base = `${label} Copy`

  if (!taken.has(base)) return base

  let number = 2

  while (taken.has(`${label} Copy ${number}`)) number += 1

  return `${label} Copy ${number}`
}

function pageMatchesView (page, pages, view, spreadsheets) {

  const viewSheet = view && view.spreadSheet

  if (!viewSheet) return true

  return recordSpreadSheetsOnPage(page, pages).some((sheet) => {

    return sheet === viewSheet || pageRecordRelationship(spreadsheets, viewSheet, sheet)
  })
}

function viewTransferDestinations (input) {

  const pages = input && input.pages || []
  const mode = input && input.mode
  const sourcePageUniqueTitle = input && input.sourcePageUniqueTitle
  const view = input && input.view

  const matches = pages.filter((page) => {

    if (!page || !page.uniqueTitle) return false

    if (mode === 'move' && page.uniqueTitle === sourcePageUniqueTitle) return false

    return pageMatchesView(page, pages, view, input && input.spreadsheets)
  })

  matches.sort((left, right) => {

    if (left.uniqueTitle === sourcePageUniqueTitle) return -1

    if (right.uniqueTitle === sourcePageUniqueTitle) return 1

    const leftTitle = String(left.title || left.uniqueTitle)
    const rightTitle = String(right.title || right.uniqueTitle)

    return leftTitle.localeCompare(rightTitle)
  })

  return matches
}

function titleErrorFor (uniqueTitle, sourceUniqueTitle, taken) {

  if (!uniqueTitle) return TITLE_REQUIRED

  if (!IDENTIFIER.test(uniqueTitle)) return TITLE_INVALID

  if (uniqueTitle === sourceUniqueTitle) return TITLE_REUSE

  if (taken.has(uniqueTitle)) return TITLE_COLLISION

  return ''
}

function pageRecordLimit (spreadsheets, view, destination, pages) {

  const pageRecord = findPageRecordSpreadSheet(pages, destination)
  const relationship = pageRecordRelationship(spreadsheets, view && view.spreadSheet, pageRecord)

  if (!relationship) {

    return { filterByPageRecord: false, relationshipTitle: '' }
  }

  return {
    filterByPageRecord: true,
    relationshipTitle: relationship.columnTitle || ''
  }
}

function emptyPlan (error) {

  return {
    ok: false,
    error,
    titleError: '',
    pageError: error,
    uniqueTitle: '',
    title: '',
    filterByPageRecord: false,
    clearsPageRecord: false,
    relationshipTitle: '',
    mode: '',
    destinationPageUniqueTitle: '',
    view: null
  }
}

function planViewTransfer (input) {

  const pages = input && input.pages || []
  const spreadsheets = input && input.spreadsheets || []
  const sourcePageUniqueTitle = input && input.sourcePageUniqueTitle
  const viewUniqueTitle = input && input.viewUniqueTitle
  const mode = input && input.mode
  const source = pages.find((page) => page && page.uniqueTitle === sourcePageUniqueTitle)

  if (!source) return emptyPlan(PAGE_MISSING)

  const view = findView(source, viewUniqueTitle)

  if (!view) return emptyPlan(VIEW_MISSING)

  if (mode !== 'duplicate' && mode !== 'move') return emptyPlan(MODE_INVALID)

  const destinations = viewTransferDestinations({
    pages,
    spreadsheets,
    sourcePageUniqueTitle,
    view,
    mode
  })
  const destinationId = input && input.destinationPageUniqueTitle
    ? String(input.destinationPageUniqueTitle)
    : ''
  let pageError = ''
  let destination = null

  if (!destinationId) {

    pageError = destinations.length ? CHOOSE_PAGE : NO_PAGE
  } else if (mode === 'move' && destinationId === sourcePageUniqueTitle) {

    pageError = DIFFERENT_PAGE
  } else {

    destination = pages.find((page) => page && page.uniqueTitle === destinationId) || null

    if (!destination) pageError = PAGE_MISSING
    else if (!destinations.some((page) => page.uniqueTitle === destinationId)) pageError = PAGE_INELIGIBLE
  }

  const taken = takenComponentUniqueTitles(pages)

  if (view.uniqueTitle) taken.add(String(view.uniqueTitle))

  const provided = input && input.uniqueTitle != null ? String(input.uniqueTitle).trim() : null
  const uniqueTitle = provided == null ? suggestedViewUniqueTitle(pages, view.uniqueTitle) : provided
  const titleError = titleErrorFor(uniqueTitle, view.uniqueTitle ? String(view.uniqueTitle) : '', taken)
  const displayTitle = mode === 'move'
    ? (view.title ? String(view.title) : '')
    : nextViewTitle(view.title, takenViewTitles(pages))
  const limit = destination && !pageError
    ? pageRecordLimit(spreadsheets, view, destination, pages)
    : { filterByPageRecord: false, relationshipTitle: '' }
  const clearsPageRecord = !pageError && !limit.filterByPageRecord && !!view.filterByPageRecord
  const prepared = titleError || pageError
    ? null
    : prepareView(view, {
      uniqueTitle,
      title: displayTitle,
      filterByPageRecord: limit.filterByPageRecord
    })

  return {
    ok: !titleError && !pageError,
    error: pageError || titleError || '',
    titleError,
    pageError,
    uniqueTitle,
    title: displayTitle,
    filterByPageRecord: limit.filterByPageRecord,
    clearsPageRecord,
    relationshipTitle: limit.relationshipTitle,
    mode,
    destinationPageUniqueTitle: destination ? destination.uniqueTitle : '',
    view: prepared
  }
}

function prepareView (view, change) {

  const copy = JSON.parse(JSON.stringify(view))

  copy.uniqueTitle = change.uniqueTitle

  if (change.title) copy.title = change.title
  else delete copy.title

  if (change.filterByPageRecord) copy.filterByPageRecord = true
  else delete copy.filterByPageRecord

  return copy
}

function detachView (page, viewUniqueTitle) {

  let removed = null

  function walk (components) {

    if (!Array.isArray(components) || removed) return

    for (let index = 0; index < components.length; index += 1) {

      const component = components[index]

      if (!component) continue

      if (component.uniqueTitle === viewUniqueTitle && isViewType(component.type)) {

        removed = components.splice(index, 1)[0]
        return
      }

      walk(component.components)

      if (removed) return
    }
  }

  walk(page && page.components)

  return removed
}

function placeView (page, view) {

  if (!Array.isArray(page.components)) page.components = []

  let row = page.components.find((component) => component && component.type === 'row')

  if (!row) {

    row = { type: 'row', components: [] }
    page.components.push(row)
  }

  if (!Array.isArray(row.components)) row.components = []

  let column = row.components.find((component) => component && component.type === 'column')

  if (!column) {

    column = { type: 'column', components: [] }
    row.components.push(column)
  }

  if (!Array.isArray(column.components)) column.components = []

  column.components.push(view)
}

function applyViewTransfer (input) {

  const plan = planViewTransfer(input)

  if (!plan.ok) {

    return {
      ok: false,
      error: plan.error,
      changedPages: [],
      view: null
    }
  }

  const pages = (input.pages || []).map((page) => JSON.parse(JSON.stringify(page)))
  const source = pages.find((page) => page && page.uniqueTitle === input.sourcePageUniqueTitle)
  const destination = pages.find((page) => page && page.uniqueTitle === plan.destinationPageUniqueTitle)

  if (plan.mode === 'move') detachView(source, input.viewUniqueTitle)

  const view = JSON.parse(JSON.stringify(plan.view))

  placeView(destination, view)

  const changedPages = []

  if (plan.mode === 'move' && source) changedPages.push(source)

  if (destination && !changedPages.includes(destination)) changedPages.push(destination)

  return {
    ok: true,
    error: '',
    changedPages,
    view
  }
}

module.exports = {
  isViewType,
  suggestedViewUniqueTitle,
  viewTransferDestinations,
  planViewTransfer,
  applyViewTransfer,
  TITLE_REQUIRED,
  TITLE_REUSE,
  TITLE_COLLISION,
  TITLE_INVALID,
  CHOOSE_PAGE,
  DIFFERENT_PAGE,
  PAGE_INELIGIBLE,
  NO_PAGE,
  VIEW_MISSING,
  PAGE_MISSING,
  MODE_INVALID
}
